import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { confirmServicePayment } from "../api/freelancerServices";
import logger from "../utils/logger.js";

export default function ServicePaymentSuccess() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function confirm() {
      const sessionId = searchParams.get("session_id");
      const token = searchParams.get("token");
      const provider = (searchParams.get("provider") || (token && !sessionId ? "paypal" : "stripe")).toLowerCase();
      const mode = searchParams.get("mode") || "";

      if (!sessionId && !token) {
        setError(isAr ? "معرّف جلسة الدفع غير موجود." : "Payment session ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const data = await confirmServicePayment({ sessionId, token, provider, mode });
        if (!active) return;
        setResult(data);
      } catch (err) {
        logger.error("Service payment success error", err);
        if (!active) return;
        setError(err?.message || (isAr ? "تعذر تأكيد الدفع." : "Unable to confirm payment."));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    confirm();

    return () => {
      active = false;
    };
  }, [searchParams, isAr]);

  return (
    <div className="container py-5" dir={isAr ? "rtl" : "ltr"}>
      <div className="row justify-content-center">
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4 p-lg-5 text-center">
              {loading ? (
                <>
                  <div className="spinner-border text-primary mb-3" role="status" />
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "جارِ تأكيد الدفع..." : "Confirming payment..."}
                  </h1>
                </>
              ) : error ? (
                <>
                  <div className="display-6 mb-3">!</div>
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "تعذر تأكيد الدفع" : "Payment confirmation failed"}
                  </h1>
                  <p className="text-muted mb-4">{error}</p>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <Link to="/freelancers" className="btn btn-outline-dark rounded-pill px-4">
                      {isAr ? "العودة للخدمات" : "Back to Services"}
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="display-6 mb-3">✓</div>
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "تم تأكيد الطلب والدفع" : "Payment and order confirmed"}
                  </h1>
                  <p className="text-muted mb-4">
                    {isAr
                      ? "تم إنشاء طلب الخدمة بنجاح، ويمكنك متابعة حالته من صفحة طلباتي."
                      : "Your service order was created successfully, and you can track it from My Service Orders."}
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary rounded-pill px-4"
                      onClick={() => navigate("/my-service-orders")}
                    >
                      {isAr ? "عرض طلباتي" : "View My Orders"}
                    </button>
                    <Link to="/freelancers" className="btn btn-outline-dark rounded-pill px-4">
                      {isAr ? "سوق المستقلين" : "Marketplace"}
                    </Link>
                  </div>

                  {result ? (
                    <div className="alert alert-light border rounded-4 mt-4 text-start">
                      <div>
                        <strong>{isAr ? "رقم الطلب:" : "Order ID:"}</strong> {result.order_id}
                      </div>
                      <div>
                        <strong>{isAr ? "الحالة:" : "Status:"}</strong> {result.status}
                      </div>
                      <div>
                        <strong>{isAr ? "حالة الدفع:" : "Payment status:"}</strong> {result.payment_status}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
